import { useEffect, useRef, useState } from "react";
import { tx, txr } from "../lib/tx";

/** Cumulative horizontal+vertical drag distance (px) that counts as "practiced". */
const PRACTICE_THRESHOLD = 260;

/**
 * Onboarding overlay teaching the core 3D interaction this course requires:
 * click-and-hold, then move the mouse to look around. Blocks the scene until
 * the learner actually performs a drag (a live demo box visibly rotates as
 * proof), not just clicks through text.
 *
 * INTENTIONALLY shown every playthrough (2026-07-13) -- an earlier version
 * persisted a "seen" flag in localStorage so it only ever fired once per
 * browser. That made it effectively invisible during manual re-testing
 * (clearing HTTP cache / hard-reloading does NOT clear localStorage, so it
 * looked broken even though it was working as designed) and was explicitly
 * reversed per product decision: this tutorial only exists in the new 3D
 * course today, so it should always reappear, not just on a learner's very
 * first pano course ever. `tutorialDismissed` in App.tsx still keeps it from
 * repeating on every qualifying beat WITHIN one playthrough.
 */
const WALK_KEYS = new Set(["w", "a", "s", "d", "arrowup", "arrowdown", "arrowleft", "arrowright"]);

export function LookAroundTutorial({
  onDone,
  variant = "look",
  canSwitchMode = false,
}: {
  onDone: () => void;
  /** "look" = drag-to-look only (pano courses); "walk" = also teach W/A/S/D. */
  variant?: "look" | "walk";
  /** Show the "you can switch modes anytime" note (courses with allowFreeExplore). */
  canSwitchMode?: boolean;
}) {
  const isWalk = variant === "walk";
  const [practiced, setPracticed] = useState(0);
  const [walked, setWalked] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [demoAngle, setDemoAngle] = useState(0);
  const last = useRef<{ x: number; y: number } | null>(null);
  // Walk mode is satisfied by EITHER a look-drag OR pressing a movement key, so a
  // mouse-only or keyboard-only learner can always continue.
  const done = practiced >= PRACTICE_THRESHOLD || (isWalk && walked);

  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      last.current = { x: e.clientX, y: e.clientY };
      setDragging(true);
    };
    const onMove = (e: PointerEvent) => {
      if (!last.current) return;
      const dx = e.clientX - last.current.x;
      const dy = e.clientY - last.current.y;
      last.current = { x: e.clientX, y: e.clientY };
      const dist = Math.abs(dx) + Math.abs(dy);
      if (dist > 0) {
        setPracticed((p) => Math.min(PRACTICE_THRESHOLD, p + dist));
        setDemoAngle((a) => Math.max(-38, Math.min(38, a + dx * 0.25)));
      }
    };
    const onUp = () => {
      last.current = null;
      setDragging(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (isWalk && WALK_KEYS.has(e.key.toLowerCase())) setWalked(true);
    };
    window.addEventListener("pointerdown", onDown, { capture: true });
    window.addEventListener("pointermove", onMove, { capture: true });
    window.addEventListener("pointerup", onUp, { capture: true });
    window.addEventListener("keydown", onKey, { capture: true });
    return () => {
      window.removeEventListener("pointerdown", onDown, { capture: true } as never);
      window.removeEventListener("pointermove", onMove, { capture: true } as never);
      window.removeEventListener("pointerup", onUp, { capture: true } as never);
      window.removeEventListener("keydown", onKey, { capture: true } as never);
    };
  }, [isWalk]);

  return (
    <div className="lat-overlay" role="dialog" aria-label={tx("How to get around")}>
      <div className="lat-card">
        <div className="lat-kicker">{tx("Before you start")}</div>
        <h2>{isWalk ? tx("How to move around") : tx("Click and drag to look around")}</h2>
        <p>
          {tx("This is a real 3D space.")}{" "}
          {isWalk ? (
            <>
              {txr("Use <0>W / A / S / D</0> (or the on-screen pad) to walk, and <1>click and drag</1> to look around — try the box below.", {}, [<strong />, <strong />])}
            </>
          ) : (
            <>
              {tx("Press and hold anywhere, then move your mouse to turn and look around the room — try it on the box below.")}
            </>
          )}
        </p>

        {isWalk && (
          <div className="lat-keys" aria-hidden>
            <span className={`lat-key ${walked ? "on" : ""}`}>{tx("W")}</span>
            <span className={`lat-key ${walked ? "on" : ""}`}>{tx("A")}</span>
            <span className={`lat-key ${walked ? "on" : ""}`}>{tx("S")}</span>
            <span className={`lat-key ${walked ? "on" : ""}`}>{tx("D")}</span>
          </div>
        )}

        <div className={`lat-demo ${dragging ? "on" : ""}`}>
          <div className="lat-demo-room">
            <div className="lat-demo-box" style={{ transform: `rotateY(${demoAngle}deg)` }}>
              <span className="lat-demo-face front" />
              <span className="lat-demo-face side" />
            </div>
          </div>
          {!done && (
            <div className="lat-demo-hint">
              <span className="lat-demo-cursor" aria-hidden>
                {"\u{1F5B1}"}
              </span>
              {tx("Hold & drag here")}
            </div>
          )}
        </div>

        <div className="lat-progress">
          <div className="lat-progress-track">
            <div className="lat-progress-fill" style={{ width: `${done ? 100 : (practiced / PRACTICE_THRESHOLD) * 100}%` }} />
          </div>
          <span>{done ? tx("Got it!") : isWalk ? tx("Drag the box or press a key") : tx("Try dragging the box")}</span>
        </div>

        {canSwitchMode && (
          <div className="lat-modenote">
            {tx("You can switch between the guided tour and free movement anytime from the controls at the bottom.")}
          </div>
        )}

        <div className="lat-actions">
          <button type="button" className="btn" disabled={!done} onClick={onDone}>
            {done ? tx("Continue") : isWalk ? tx("Try it to continue") : tx("Drag to continue")}
          </button>
          <button type="button" className="btn btn-ghost" onClick={onDone}>
            {tx("Skip")}
          </button>
        </div>
      </div>
    </div>
  );
}
